import React from 'react';
import { Card, CardHeader, CardBody, CardFooter, InfoCard } from '../../components/cards';
import { PrimaryButton, SecondaryButton } from '../../components/buttons';
import { AccountIcon, AnalyticsIcon, GlobeIcon } from '../../icons';

/**
 * CardShowcase
 * Displays card components with various configurations
 */
import { surfaces, textColors, typography, borders } from '../../tokens';
const CardShowcase = () => {
  const CodeBlock = ({ code }) => (
    <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-xs overflow-x-auto">
      <code>{code}</code>
    </pre>
  );

  return (
    <div className="space-y-12">
      {/* Card Variants */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Card Variants</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Cards with different visual styles for various use cases.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card variant="default" padding="md">
            <CardHeader title="Default Card" subtitle="No border or shadow" />
            <CardBody>
              <p className="${textColors.secondary}">
                This is the default card variant with standard padding and no special styling.
              </p>
            </CardBody>
          </Card>

          <Card variant="bordered" padding="md">
            <CardHeader title="Bordered Card" subtitle="With visible border" />
            <CardBody>
              <p className="${textColors.secondary}">
                This card has a border for clear separation from the background.
              </p>
            </CardBody>
          </Card>

          <Card variant="elevated" padding="md">
            <CardHeader title="Elevated Card" subtitle="With shadow" />
            <CardBody>
              <p className="${textColors.secondary}">
                This card uses shadow to create depth and visual hierarchy.
              </p>
            </CardBody>
          </Card>

          <Card variant="flat" padding="md">
            <CardHeader title="Flat Card" subtitle="Minimal styling" />
            <CardBody>
              <p className="${textColors.secondary}">
                This card has minimal styling without border or shadow.
              </p>
            </CardBody>
          </Card>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Card } from '../design-system';

<Card variant="bordered" padding="md">
  Card content here
</Card>`} />
        </div>
      </section>

      {/* Card with Subcomponents */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Card Composition</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Cards with header, body, and footer sections.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card variant="bordered" padding="md">
            <CardHeader 
              title="Project Settings" 
              subtitle="Manage your project configuration"
            />
            <CardBody>
              <p className="${textColors.secondary} mb-4">
                Configure the settings for your project. Changes will be saved automatically.
              </p>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm font-medium ${textColors.primary}">Status</span>
                  <span className="text-sm ${textColors.secondary}">Active</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm font-medium ${textColors.primary}">Members</span>
                  <span className="text-sm ${textColors.secondary}">5</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-sm font-medium ${textColors.primary}">Created</span>
                  <span className="text-sm ${textColors.secondary}">2 days ago</span>
                </div>
              </div>
            </CardBody>
            <CardFooter>
              <SecondaryButton size="sm">Cancel</SecondaryButton>
              <PrimaryButton size="sm">Save Changes</PrimaryButton>
            </CardFooter>
          </Card>

          <Card variant="elevated" padding="md">
            <CardHeader 
              title="Quick Actions" 
              subtitle="Common tasks and shortcuts"
            />
            <CardBody>
              <div className="grid grid-cols-2 gap-3">
                <button className="p-4 text-left rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <div className="text-sm font-medium ${textColors.brightest}">Create Website</div>
                  <div className="text-xs text-gray-500 mt-1">Start from scratch</div>
                </button>
                <button className="p-4 text-left rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <div className="text-sm font-medium ${textColors.brightest}">Use Template</div>
                  <div className="text-xs text-gray-500 mt-1">Choose a design</div>
                </button>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Card, CardHeader, CardBody, CardFooter } from '../design-system';

<Card variant="bordered" padding="md">
  <CardHeader 
    title="Card Title" 
    subtitle="Card subtitle"
  />
  <CardBody>
    Card content goes here
  </CardBody>
  <CardFooter>
    <SecondaryButton>Cancel</SecondaryButton>
    <PrimaryButton>Confirm</PrimaryButton>
  </CardFooter>
</Card>`} />
        </div>
      </section>

      {/* Interactive Cards */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Interactive Cards</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Cards with hover and click interactions.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card 
            variant="bordered" 
            padding="md" 
            hoverable 
            onClick={() => alert('Card clicked!')}
          >
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <GlobeIcon size="lg" color="primary" />
              </div>
              <h3 className="font-heading font-bold ${textColors.brightest} mb-1">Hoverable Card</h3>
              <p className="text-sm ${textColors.secondary}">Click me to see action</p>
            </div>
          </Card>

          <Card 
            variant="bordered" 
            padding="md" 
            hoverable 
            selected
          >
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AnalyticsIcon size="lg" color="success" />
              </div>
              <h3 className="font-heading font-bold ${textColors.brightest} mb-1">Selected Card</h3>
              <p className="text-sm ${textColors.secondary}">Currently active</p>
            </div>
          </Card>

          <Card 
            variant="bordered" 
            padding="md" 
            hoverable
          >
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AccountIcon size="lg" color="secondary" />
              </div>
              <h3 className="font-heading font-bold ${textColors.brightest} mb-1">Another Card</h3>
              <p className="text-sm ${textColors.secondary}">Hover to see effect</p>
            </div>
          </Card>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Card } from '../design-system';

<Card 
  variant="bordered" 
  padding="md" 
  hoverable 
  selected={isSelected}
  onClick={handleClick}
>
  Card content
</Card>`} />
        </div>
      </section>

      {/* InfoCard */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>InfoCard</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Specialized cards for displaying metrics and stats.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <InfoCard
            title="Total Users"
            value="1,234"
            description="+12% from last month"
            icon={<AccountIcon size="lg" />}
            trend={{ value: 12, isPositive: true }}
          />

          <InfoCard
            title="Revenue"
            value="$45,678"
            description="-5% from last month"
            icon={<AnalyticsIcon size="lg" />}
            trend={{ value: 5, isPositive: false }}
          />

          <InfoCard
            title="Active Projects"
            value="89"
            description="Across all teams"
            icon={<GlobeIcon size="lg" />}
          />

          <InfoCard
            title="Completion Rate"
            value="94.5%"
            description="+3% this week"
            trend={{ value: 3, isPositive: true }}
          />
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { InfoCard } from '../design-system';

<InfoCard
  title="Total Users"
  value="1,234"
  description="+12% from last month"
  icon={<AccountIcon size="lg" />}
  trend={{ value: 12, isPositive: true }}
/>`} />
        </div>
      </section>

      {/* Padding Options */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Padding Options</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Cards support different padding sizes.
        </p>

        <div className="space-y-4 mb-6">
          <Card variant="bordered" padding="sm">
            <p className="${textColors.secondary}">Small padding (sm)</p>
          </Card>

          <Card variant="bordered" padding="md">
            <p className="${textColors.secondary}">Medium padding (md) - default</p>
          </Card>

          <Card variant="bordered" padding="lg">
            <p className="${textColors.secondary}">Large padding (lg)</p>
          </Card>

          <Card variant="bordered" padding="none">
            <p className="${textColors.secondary} p-4">No padding - manual padding applied</p>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default CardShowcase;
